import { useCallback, useEffect, useRef } from 'react';
import { updateMatrixRoomsHasMessage } from '../../../../shared/actions/matrixAction';
import { isAbortError } from '../../../../shared/utils/isAbortError';
import { captureChatException } from 'shared/sentry';

/**
 * PATCH `/matrix/rooms-update-hasmessage` for rooms missing backend user metadata.
 * Runs only after every paginated `/matrix/rooms` page has loaded so page-2+ rooms
 * are not mistaken for rooms that need patching.
 */
export function useMatrixRoomsHasMessageSync({
  enabled,
  isFetching,
  allPagesLoaded,
  nullUserIdRoomIds,
  onPatchSuccess,
}) {
  const patchedRoomIdsRef = useRef(new Set());
  const patchInFlightRef = useRef(false);

  const nullUserIdsKey = nullUserIdRoomIds.join('|');

  useEffect(() => {
    if (!enabled || isFetching || !allPagesLoaded) return undefined;
    if (!nullUserIdRoomIds.length) return undefined;
    if (patchInFlightRef.current) return undefined;

    const pendingRoomIds = nullUserIdRoomIds.filter(
      (roomId) => !patchedRoomIdsRef.current.has(roomId),
    );
    if (!pendingRoomIds.length) return undefined;

    let cancelled = false;
    patchInFlightRef.current = true;

    pendingRoomIds.forEach((roomId) => patchedRoomIdsRef.current.add(roomId));

    (async () => {
      try {
        const result = await updateMatrixRoomsHasMessage(pendingRoomIds);
        if (cancelled) return;

        if (!result.success) {
          pendingRoomIds.forEach((roomId) => patchedRoomIdsRef.current.delete(roomId));
          return;
        }

        if (typeof onPatchSuccess === 'function') {
          await onPatchSuccess();
        }
      } catch (error) {
        if (!cancelled) {
          pendingRoomIds.forEach((roomId) => patchedRoomIdsRef.current.delete(roomId));
          if (!isAbortError(error)) {
            captureChatException(error, {
              tags: { feature: 'messages', matrix: 'rooms-has-message-update' },
            });
          }
        }
      } finally {
        patchInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    isFetching,
    allPagesLoaded,
    nullUserIdsKey,
    nullUserIdRoomIds,
    onPatchSuccess,
  ]);

  const resetPatchedRoomIds = useCallback(() => {
    patchedRoomIdsRef.current.clear();
  }, []);

  return { resetPatchedRoomIds };
}
